import { useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import classNames from "classnames"
import Navbar from "@/Components/Navbar"
import backgroundImage from "@/images/backgrounds/lobby-background.webp"

const M: React.FC = () => <span className="font-fira-sans">M</span>

const SectionCard: React.FC<{
  id: string
  icon: string
  title: string
  color: string
  children: React.ReactNode
}> = ({ id, icon, title, color, children }) => (
  <section
    id={id}
    className="group relative scroll-mt-24 overflow-hidden rounded-2xl border border-white/10 bg-white/[0.035] backdrop-blur-sm transition-all duration-300 hover:border-white/20"
  >
    {/* Top accent line + soft color glow */}
    <div
      className="pointer-events-none absolute inset-x-0 top-0 h-[2px]"
      style={{ background: `linear-gradient(90deg, transparent, ${color}aa, transparent)` }}
    />
    <div
      className="pointer-events-none absolute -top-20 -left-16 h-40 w-40 rounded-full opacity-[0.12] blur-3xl"
      style={{ backgroundColor: color }}
    />
    <div className="relative p-6 sm:p-8">
      <div className="flex items-center gap-4">
        <span
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ring-1 ring-white/10"
          style={{
            background: `linear-gradient(135deg, ${color}33, ${color}14)`,
            color,
            boxShadow: `0 10px 30px -12px ${color}90`,
          }}
        >
          <i className={`fas ${icon} text-xl`} />
        </span>
        <h2 className="text-2xl font-bold text-white tracking-tight">{title}</h2>
      </div>
      <div
        className="mt-5 mb-6 h-px w-full"
        style={{ background: `linear-gradient(90deg, ${color}55, transparent)` }}
      />
      {children}
    </div>
  </section>
)

const sectionMeta = [
  { id: "start", label: "Start Here", icon: "fa-rocket" },
  { id: "objective", label: "Goal", icon: "fa-bullseye" },
  { id: "flow", label: "Turn Flow", icon: "fa-arrows-rotate" },
  { id: "auction", label: "Auctions", icon: "fa-gavel" },
  { id: "properties", label: "Properties", icon: "fa-house" },
  { id: "roles", label: "Roles", icon: "fa-masks-theater" },
  { id: "cards", label: "Action Cards", icon: "fa-layer-group" },
  { id: "stock", label: "Stock Exchange", icon: "fa-chart-line" },
  { id: "decision", label: "Decision Hub", icon: "fa-bolt" },
  { id: "trading", label: "Trading", icon: "fa-handshake" },
  { id: "winning", label: "Winning", icon: "fa-trophy" },
  { id: "tips", label: "Pro Tips", icon: "fa-lightbulb" },
]

const HowToPlay: React.FC = () => {
  const nav = useNavigate()
  const scrollRef = useRef<HTMLDivElement>(null)
  const [activeId, setActiveId] = useState("start")

  // Highlight the section currently in view (scrollspy).
  useEffect(() => {
    const root = scrollRef.current
    if (!root) return
    const sections = sectionMeta
      .map((s) => document.getElementById(s.id))
      .filter((el): el is HTMLElement => el !== null)

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
        if (visible[0]) setActiveId(visible[0].target.id)
      },
      { root, rootMargin: "-15% 0px -75% 0px", threshold: 0 }
    )
    sections.forEach((s) => observer.observe(s))
    return () => observer.disconnect()
  }, [])

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  return (
    <div
      className="w-full h-screen bg-cover bg-fixed overflow-hidden"
      style={{ backgroundImage: `url(${backgroundImage})` }}
    >
      <div className="h-screen flex flex-col bg-black/70">
        <Navbar />

        {/* Scrollable content */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto">
          {/* Sticky top bar */}
          <div className="sticky top-0 z-30 bg-black/80 backdrop-blur-md border-b border-white/10">
            <div className="max-w-[1400px] mx-auto pl-[max(1.5rem,var(--safe-left))] pr-[max(1.5rem,var(--safe-right))] py-3 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <button
                  onClick={() => nav(-1)}
                  className="flex items-center justify-center h-10 w-10 bg-white/10 hover:bg-white/20 rounded-lg text-white transition-colors cursor-pointer shrink-0"
                  aria-label="Back"
                >
                  <i className="fas fa-arrow-left" />
                </button>
                <div className="min-w-0">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.25em] text-amber-300/70">
                    Game Guide
                  </div>
                  <h1 className="text-lg font-bold text-white leading-tight truncate">How to Play Dealmaster</h1>
                </div>
              </div>
              <button
                onClick={() => nav("/lobby")}
                className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-400 hover:to-yellow-500 text-white font-semibold text-sm shadow-lg transition-all duration-200 hover:scale-[1.03] shrink-0"
              >
                <i className="fas fa-play text-xs" />
                Start Playing
              </button>
            </div>

            {/* Mobile section pills */}
            <div className="lg:hidden border-t border-white/10 pl-[max(1rem,var(--safe-left))] pr-[max(1rem,var(--safe-right))] py-2 overflow-x-auto no-scrollbar">
              <div className="flex items-center gap-2 w-max">
                {sectionMeta.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => scrollTo(s.id)}
                    className={classNames(
                      "flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 border",
                      activeId === s.id
                        ? "bg-white/20 border-white/30 text-white"
                        : "bg-white/[0.03] border-white/10 text-white/70 hover:text-white hover:bg-white/[0.08]"
                    )}
                  >
                    <i className={`fas ${s.icon} text-xs`} />
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="max-w-[1400px] mx-auto pl-[max(1.5rem,var(--safe-left))] pr-[max(1.5rem,var(--safe-right))] pt-8 pb-[max(2rem,var(--safe-bottom))] lg:grid lg:grid-cols-[240px_minmax(0,1fr)] lg:gap-10">
            {/* Sidebar (desktop) */}
            <aside className="hidden lg:block">
              <nav className="sticky top-24">
                <div className="text-[10px] font-semibold uppercase tracking-[0.25em] text-white/40 px-3 mb-2">
                  Contents
                </div>
                <div className="space-y-0.5">
                  {sectionMeta.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => scrollTo(s.id)}
                      className={classNames(
                        "group relative flex items-center gap-3 w-full px-3 py-2 rounded-lg text-left text-sm font-medium transition-colors",
                        activeId === s.id ? "bg-white/10 text-white" : "text-white/70 hover:text-white hover:bg-white/5"
                      )}
                    >
                      <span
                        className={classNames(
                          "absolute left-0 top-1/2 -translate-y-1/2 h-5 w-1 rounded-r-full bg-amber-400 transition-opacity",
                          activeId === s.id ? "opacity-100" : "opacity-0"
                        )}
                      />
                      <i
                        className={classNames(
                          "fas w-4 text-center text-[13px]",
                          s.icon,
                          activeId === s.id ? "text-amber-300" : "text-white/40 group-hover:text-white/70"
                        )}
                      />
                      {s.label}
                    </button>
                  ))}
                </div>
              </nav>
            </aside>

            {/* Content */}
            <main className="min-w-0 space-y-6">
              {/* Quick Start */}
              <section
                id="start"
                className="relative scroll-mt-24 overflow-hidden rounded-2xl border border-amber-400/30 bg-gradient-to-br from-amber-500/[0.13] via-white/[0.04] to-white/[0.02] p-6 sm:p-8"
              >
                <div className="pointer-events-none absolute -top-24 -right-16 h-56 w-56 rounded-full bg-amber-500/20 blur-3xl" />
                <div className="relative">
                  <div className="inline-flex items-center gap-2 rounded-full border border-amber-400/30 bg-amber-500/10 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.2em] text-amber-300 mb-4">
                    <i className="fas fa-rocket text-xs" /> New player? Start here
                  </div>
                  <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3 tracking-tight">
                    The game in 30 seconds
                  </h2>
                  <p className="text-white/75 leading-relaxed mb-7  text-[15px]">
                    Dealmaster is a fast, strategic board game. You move around the board buying properties, collecting
                    rent, playing action cards and making deals.{" "}
                    <span className="text-amber-300 font-semibold">Be the richest player by the end</span> — or the last
                    one standing.
                  </p>

                  {/* 3-step loop */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                    {[
                      {
                        n: 1,
                        t: "Roll & Move",
                        d: "Roll the dice and move your piece around the board.",
                        icon: "fa-dice",
                        c: "text-blue-300",
                      },
                      {
                        n: 2,
                        t: "Buy, Bid or Act",
                        d: "Land somewhere new to start an auction, draw/play cards, build houses, or trade.",
                        icon: "fa-gavel",
                        c: "text-emerald-300",
                      },
                      {
                        n: 3,
                        t: "End Your Turn",
                        d: "Wrap up before the timer ends and pass to the next player.",
                        icon: "fa-flag-checkered",
                        c: "text-fuchsia-300",
                      },
                    ].map((step) => (
                      <div
                        key={step.n}
                        className="relative bg-white/[0.05] rounded-xl p-5 border border-white/10 transition-colors hover:bg-white/[0.07]"
                      >
                        <div className="flex items-center gap-3 mb-2.5">
                          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white/10 ring-1 ring-white/15 text-sm font-bold text-white">
                            {step.n}
                          </span>
                          <i className={classNames("fas", step.icon, step.c, "text-lg")} />
                        </div>
                        <div className="text-white font-semibold">{step.t}</div>
                        <div className="text-white/70 text-sm mt-1 leading-relaxed">{step.d}</div>
                      </div>
                    ))}
                  </div>

                  {/* Key numbers */}
                  <div className="grid grid-cols-3 sm:grid-cols-3 gap-3 mt-5">
                    {[
                      {
                        v: (
                          <>
                            <M />
                            1,500
                          </>
                        ),
                        l: "Starting Cash",
                        c: "text-green-400",
                        icon: "fa-wallet",
                      },
                      {
                        v: (
                          <>
                            <M />
                            200
                          </>
                        ),
                        l: "Full Round Bonus",
                        c: "text-yellow-400",
                        icon: "fa-gift",
                      },
                      { v: "35", l: "Total Turns", c: "text-blue-400", icon: "fa-hourglass-half" },
                    ].map((k, i) => (
                      <div
                        key={i}
                        className="bg-white/[0.05] rounded-xl p-4 border border-white/10 flex items-center gap-3.5"
                      >
                        <i className={classNames("fas", k.icon, k.c, "text-2xl w-7 text-center")} />
                        <div>
                          <div className={classNames("text-xl font-bold leading-none", k.c)}>{k.v}</div>
                          <div className="text-white/50 text-xs mt-1">{k.l}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </section>

              {/* Objective */}
              <SectionCard id="objective" icon="fa-bullseye" title="Goal" color="#60A5FA">
                <p className="text-white/80 leading-relaxed">
                  Become the richest player by owning the most valuable properties and maximizing rents, playing action
                  cards at the right moments, trading cleverly, and investing wisely in the stock exchange.
                </p>
              </SectionCard>

              {/* Game Flow */}
              <SectionCard id="flow" icon="fa-arrows-rotate" title="How a Turn Works" color="#34D399">
                <div className="space-y-4">
                  {[
                    {
                      t: "Pick Your Role",
                      d: "At the start of the game, every player selects a unique role that grants special abilities throughout the game. Choose wisely — your role defines your strategy.",
                    },
                    {
                      t: "Roll & Move",
                      d: "Roll two dice and move your piece around the board. Where you land determines your next action — buy, auction, draw cards, or pay up.",
                    },
                    {
                      t: "Take Actions",
                      d: "During your turn, you can trade properties with other players, build houses on your complete color sets, play action cards, or invest in the stock exchange.",
                    },
                    {
                      t: "End Your Turn",
                      d: "Once you've completed your actions, end your turn and the next player takes over. Each turn is timed, so plan ahead!",
                    },
                  ].map((step, i) => (
                    <div key={i} className="flex gap-4 items-start">
                      <div className="w-9 h-9 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center text-emerald-300 font-bold shrink-0">
                        {i + 1}
                      </div>
                      <div>
                        <h3 className="text-white font-semibold text-lg">{step.t}</h3>
                        <p className="text-white/60 mt-1 leading-relaxed">{step.d}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </SectionCard>

              {/* Auction System */}
              <SectionCard id="auction" icon="fa-gavel" title="The Auction System" color="#F59E0B">
                <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-4 mb-4">
                  <p className="text-yellow-300/90 font-medium text-sm leading-relaxed">
                    Unlike classic board games, almost every property in Dealmaster goes through an auction. This is the
                    core mechanic that makes the game strategic and competitive.
                  </p>
                </div>
                <div className="space-y-3 text-white/70 leading-relaxed">
                  <p>
                    When a player lands on an unowned property, a{" "}
                    <span className="text-yellow-400 font-semibold">hidden auction</span> begins where all players bid
                    simultaneously. Nobody can see what others are bidding — you submit your bid once and the highest
                    bidder wins.
                  </p>
                  <ul className="space-y-2 ml-1">
                    {[
                      <>All players can participate regardless of who landed on the property</>,
                      <>
                        Minimum bid is <M />1
                      </>,
                      <>If bids are tied, the auction continues with another round</>,
                      <>
                        The{" "}
                        <span className="text-amber-300">"Buy property on landing without auction" action card</span>{" "}
                        lets you skip the auction and buy at base price
                      </>,
                    ].map((li, i) => (
                      <li key={i} className="flex gap-2.5 items-start">
                        <i className="fas fa-circle text-[5px] text-yellow-400 mt-2" />
                        <span>{li}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </SectionCard>

              {/* Properties & Building */}
              <SectionCard id="properties" icon="fa-house" title="Properties & Building" color="#FB923C">
                <div className="space-y-4 text-white/70 leading-relaxed">
                  <p>
                    Properties are grouped into <span className="text-orange-400 font-semibold">8 color sets</span> with
                    2 properties each, plus a special group of 4 utility properties. Once you own all properties in a
                    color set, you can start building.
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="bg-white/[0.04] rounded-xl p-4 border border-white/10">
                      <h4 className="text-white font-semibold mb-2 flex items-center gap-2">
                        <i className="fas fa-hammer text-orange-400 text-sm" /> Building Rules
                      </h4>
                      <ul className="space-y-1.5 text-sm text-white/60">
                        <li>• Max 3 houses per property</li>
                        <li>• Must own the complete color set</li>
                      </ul>
                    </div>
                    <div className="bg-white/[0.04] rounded-xl p-4 border border-white/10">
                      <h4 className="text-white font-semibold mb-2 flex items-center gap-2">
                        <i className="fas fa-coins text-orange-400 text-sm" /> Rent Collection
                      </h4>
                      <ul className="space-y-1.5 text-sm text-white/60">
                        <li>• Rent increases with each house built</li>
                        <li>• Action cards can double or negate rent</li>
                        <li>• The Rent Lord role earns 50% more rent</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </SectionCard>

              {/* Roles */}
              <SectionCard id="roles" icon="fa-masks-theater" title="Roles" color="#A78BFA">
                <p className="text-white/70 mb-5 leading-relaxed">
                  Every player picks a unique role at the start of the game. Each role provides a powerful passive
                  ability that shapes your entire strategy.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    {
                      name: "The Financier",
                      desc: (
                        <>
                          Start with +<M />
                          500 bonus cash and earn <M />
                          300 (instead of <M />
                          200) every time you complete a board round.
                        </>
                      ),
                      color: "from-green-500/20 to-green-600/10",
                      border: "border-green-500/30",
                      icon: "fa-sack-dollar",
                    },
                    {
                      name: "The Trickster",
                      desc: "Draw 2 action cards instead of 1 when landing on action tiles.",
                      color: "from-purple-500/20 to-purple-600/10",
                      border: "border-purple-500/30",
                      icon: "fa-wand-magic-sparkles",
                    },
                    {
                      name: "The Rent Lord",
                      desc: "Collect 50% more rent on all your properties.",
                      color: "from-red-500/20 to-red-600/10",
                      border: "border-red-500/30",
                      icon: "fa-crown",
                    },
                    {
                      name: "Auction Strategist",
                      desc: (
                        <>
                          Pay <M />
                          100 less on every auction you win. Outbid smarter, not harder.
                        </>
                      ),
                      color: "from-yellow-500/20 to-yellow-600/10",
                      border: "border-yellow-500/30",
                      icon: "fa-gavel",
                    },
                    {
                      name: "Wall Street Wolf",
                      desc: "Earn double returns on the stock exchange. Turn investments into a fortune.",
                      color: "from-blue-500/20 to-blue-600/10",
                      border: "border-blue-500/30",
                      icon: "fa-chart-line",
                    },
                    {
                      name: "Private Buyer",
                      desc: "Buy up to 4 properties at base price without auction. Secure key properties early.",
                      color: "from-teal-500/20 to-teal-600/10",
                      border: "border-teal-500/30",
                      icon: "fa-key",
                    },
                  ].map((role) => (
                    <div
                      key={role.name}
                      className={`bg-gradient-to-r ${role.color} rounded-xl p-4 border ${role.border} flex gap-4 items-center`}
                    >
                      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-white/10 ring-1 ring-white/15">
                        <i className={`fas ${role.icon} text-lg text-white`} />
                      </span>
                      <div>
                        <h4 className="text-white font-semibold">{role.name}</h4>
                        <p className="text-white/60 text-sm mt-0.5 leading-relaxed">{role.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </SectionCard>

              {/* Action Cards */}
              <SectionCard id="cards" icon="fa-layer-group" title="Action Cards" color="#F472B6">
                <p className="text-white/70 mb-4 leading-relaxed">
                  Land on an <span className="text-pink-400 font-semibold">Action Card tile</span> to draw a card.
                  Unlike traditional board games, you <span className="text-white font-medium">keep your cards</span>{" "}
                  and play them strategically during the game. There are 8 action card tiles on the board.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="bg-white/[0.04] rounded-xl p-4 border border-white/10">
                    <h4 className="text-pink-300 font-semibold text-sm mb-2 flex items-center gap-2">
                      <i className="fas fa-bolt-lightning" /> Offensive Cards
                    </h4>
                    <ul className="space-y-1.5 text-xs text-white/60 leading-relaxed">
                      <li>
                        <span className="text-white/80">Double Rent</span> — Double rent when someone lands on your
                        property
                      </li>
                      <li>
                        <span className="text-white/80">Forced 20% Tax</span> — Force a player to pay 20% of their cash to
                        the bank
                      </li>
                      <li>
                        <span className="text-white/80">Property Tax</span> — Target player pays per property owned
                      </li>
                      <li>
                        <span className="text-white/80">House Tax</span> — Target player pays <M />
                        50 per house owned
                      </li>
                      <li>
                        <span className="text-white/80">Force Downgrade</span> — Remove a house from any opponent's
                        property
                      </li>
                    </ul>
                  </div>
                  <div className="bg-white/[0.04] rounded-xl p-4 border border-white/10">
                    <h4 className="text-green-300 font-semibold text-sm mb-2 flex items-center gap-2">
                      <i className="fas fa-shield-halved" /> Utility Cards
                    </h4>
                    <ul className="space-y-1.5 text-xs text-white/60 leading-relaxed">
                      <li>
                        <span className="text-white/80">Say No</span> — Avoid paying rent once
                      </li>
                      <li>
                        <span className="text-white/80">Forced Buy</span> — Buy a property at base price, skip the
                        auction
                      </li>
                      <li>
                        <span className="text-white/80">Advance to Start Tile</span> — Move to the board start and
                        collect <M />
                        200
                      </li>
                      <li>
                        <span className="text-white/80">Get Interest</span> — Earn 20% interest on your total cash
                      </li>
                      <li>
                        <span className="text-white/80">Swap Position</span> — Swap board position with any player
                      </li>
                      <li>
                        <span className="text-white/80">Forced Auction</span> — Force an auction on any unsold property
                      </li>
                    </ul>
                  </div>
                </div>
              </SectionCard>

              {/* Stock Exchange */}
              <SectionCard id="stock" icon="fa-chart-line" title="Stock Exchange" color="#2DD4BF">
                <div className="space-y-3 text-white/70 leading-relaxed">
                  <p>
                    When you complete a board round, you get the option to invest your cash in the{" "}
                    <span className="text-teal-400 font-semibold">stock exchange</span>. Your investment grows each time
                    you complete a round around the board.
                  </p>

                  <div className="bg-white/[0.04] rounded-xl p-4 border border-white/10">
                    <h4 className="text-white font-semibold mb-3">How It Works</h4>
                    <div className="space-y-2 text-sm">
                      {[
                        ["Invest money after completing a board round", "Money enters stock", "text-teal-400"],
                        ["Each round adds a 30% multiplier", "Value grows", "text-teal-400"],
                        ["Withdraw anytime at the board start", "Collect with profits", "text-teal-400"],
                        ["Wall Street Wolf gets 2x returns", "Double gains", "text-yellow-400"],
                      ].map(([left, right, c], i, arr) => (
                        <div
                          key={i}
                          className={classNames(
                            "flex justify-between items-center gap-3 py-1",
                            i < arr.length - 1 && "border-b border-white/5"
                          )}
                        >
                          <span className="text-white/60">{left}</span>
                          <span className={classNames("shrink-0 text-right", c as string)}>{right}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-teal-500/10 border border-teal-500/20 rounded-xl p-4">
                    <p className="text-teal-200/80 text-sm leading-relaxed">
                      <span className="font-semibold text-teal-200">Example:</span> Invest <M />
                      1,000 → Complete two board rounds → Your stock is now worth <M />
                      1,600. As the Wall Street Wolf, it would be <M />
                      3,200!
                    </p>
                  </div>
                </div>
              </SectionCard>

              {/* Decision Hub */}
              <SectionCard id="decision" icon="fa-bolt" title="Decision Hub" color="#E879F9">
                <p className="text-white/70 mb-4 leading-relaxed">
                  There are 2 <span className="text-fuchsia-400 font-semibold">Decision Hub</span> tiles on the board.
                  When you land on one, you must make a choice within 15 seconds:
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-5 text-center">
                    <i className="fas fa-hand-holding-dollar text-3xl text-green-400 mb-3" />
                    <h4 className="text-green-400 font-bold text-lg">
                      Take <M />
                      250
                    </h4>
                    <p className="text-white/50 text-sm mt-2 leading-relaxed">
                      Instant cash for immediate needs — pay rent, fund auctions, or build houses.
                    </p>
                  </div>
                  <div className="bg-purple-500/10 border border-purple-500/20 rounded-xl p-5 text-center">
                    <i className="fas fa-layer-group text-3xl text-purple-400 mb-3" />
                    <h4 className="text-purple-400 font-bold text-lg">Draw Cards</h4>
                    <p className="text-white/50 text-sm mt-2 leading-relaxed">
                      Get 2-3 action cards for long-term strategic advantage. High risk, high reward.
                    </p>
                  </div>
                </div>
              </SectionCard>

              {/* Trading */}
              <SectionCard id="trading" icon="fa-handshake" title="Trading" color="#38BDF8">
                <div className="space-y-3 text-white/70 leading-relaxed">
                  <p>
                    During your turn, you can propose trades with any other player. Trades can include{" "}
                    <span className="text-sky-400 font-semibold">properties</span> and{" "}
                    <span className="text-green-400 font-semibold">money</span>
                  </p>
                  <ul className="space-y-2 ml-1">
                    {[
                      "Both players must agree to the trade within 25 seconds",
                      "You cannot trade properties that have houses on them — sell the houses first",
                      "Completing color sets through trades is the fastest way to build houses",
                    ].map((li, i) => (
                      <li key={i} className="flex gap-2.5 items-start">
                        <i className="fas fa-circle text-[5px] text-sky-400 mt-2" />
                        <span>{li}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </SectionCard>

              {/* Winning & Bankruptcy */}
              <SectionCard id="winning" icon="fa-trophy" title="Winning & Bankruptcy" color="#4ADE80">
                <div className="space-y-4 text-white/70 leading-relaxed">
                  <p>
                    The game ends when all players except one have gone bankrupt, or after the total turn limit is
                    reached. The richest remaining player wins.
                  </p>
                  <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4">
                    <h4 className="text-red-400 font-semibold mb-2 flex items-center gap-2">
                      <i className="fas fa-triangle-exclamation text-sm" /> Bankruptcy
                    </h4>
                    <p className="text-white/60 text-sm leading-relaxed">
                      If your cash drops below <M />
                      0, you have a limited number of turns to recover by selling houses or trading properties. Fail to
                      recover and you're eliminated — your remaining properties go to your creditor or back to the bank.
                    </p>
                  </div>
                </div>
              </SectionCard>

              {/* Pro Tips */}
              <SectionCard id="tips" icon="fa-lightbulb" title="Pro Tips" color="#FBBF24">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    { tip: "Don't overbid in auctions early — save cash for building houses later.", icon: "fa-brain" },
                    {
                      tip: "Completing a color set is everything. Trade aggressively to get there.",
                      icon: "fa-bullseye",
                    },
                    { tip: "Hold your Say No card for expensive properties with houses.", icon: "fa-shield-halved" },
                    {
                      tip: "The stock exchange is most powerful when you invest early with multiple rounds ahead.",
                      icon: "fa-chart-line",
                    },
                    {
                      tip: "Private Buyer should grab properties that complete sets — don't waste your 4 picks.",
                      icon: "fa-key",
                    },
                    { tip: "Watch other players' cash — bid just enough to win, not a dollar more.", icon: "fa-eye" },
                  ].map((item, i) => (
                    <div
                      key={i}
                      className="bg-white/[0.04] rounded-xl p-4 border border-white/10 flex gap-3 items-start"
                    >
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-400/15 text-amber-300">
                        <i className={`fas ${item.icon} text-sm`} />
                      </span>
                      <p className="text-white/70 text-sm leading-relaxed">{item.tip}</p>
                    </div>
                  ))}
                </div>
              </SectionCard>

              {/* Footer CTA */}
              <div className="rounded-2xl border border-amber-400/30 bg-gradient-to-br from-amber-500/10 to-white/[0.02] p-7 text-center">
                <h3 className="text-2xl font-bold text-white mb-1">Ready to play?</h3>
                <p className="text-white/60 mb-5">That's everything you need — time to make some deals.</p>
                <button
                  onClick={() => nav("/lobby")}
                  className="inline-flex items-center gap-2 px-8 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-400 hover:to-yellow-500 text-white font-bold text-lg shadow-lg transition-all duration-200 hover:scale-[1.03]"
                >
                  <i className="fas fa-play text-sm" />
                  Start Playing
                </button>
              </div>
            </main>
          </div>
        </div>
      </div>
    </div>
  )
}

export default HowToPlay
